
import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import { CardContent } from '@/components/ui/card'
import { NodeContainer } from '../base/NodeContainer'
import { withNodeOptimization } from '../hoc/withNodeOptimization'
import { Icon } from '@/components/ui/icon'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import type { ComposedPrompt } from '@/lib/canvas/utils/promptComposer'
import { executeBatch, type BatchItem, type BatchJobParams } from '@/lib/canvas/utils/batchExecutor'

// ── Types ───────────────────────────────────────────────────────────────────

type BatchStatus = 'idle' | 'ready' | 'running' | 'paused' | 'completed' | 'cancelled' | 'error'
type MediaType = 'image' | 'video' | '3d' | 'shader' | 'sound' | 'text' | null

interface BatchProgress {
  total: number
  succeeded: number
  failed: number
  queued: number
  currentIndex: number
  currentSummary: string
  estimatedTimeRemainingMs: number
  averageTimePerItemMs: number
}

interface BatchCost {
  totalCost: number
  projectedTotal: number
}

interface BatchConfig {
  concurrency: number
  maxRetries: number
  pauseOnError: boolean
  skipCompleted: boolean
}

// ── Props ───────────────────────────────────────────────────────────────────

interface BatchGeneratorProps {
  id: string
  title?: string
  selected?: boolean
  width?: number
  height?: number
  onSelect?: () => void
  onHoverChange?: (hovered: boolean) => void
  onStateChange?: (updates: Record<string, unknown> | ((prev: Record<string, unknown>) => Record<string, unknown>)) => void
  state?: Record<string, unknown>
  connectedPrompts?: ComposedPrompt[] | null
  connectedMediaType?: MediaType
  connectedModelId?: string | null
  connectedModelName?: string | null
  connectedPurpose?: string
  connectedSubPurpose?: string
  connectedSettings?: Record<string, unknown>
  connectedNodeType?: 'image' | 'video' | 'text'
  /**
   * Set when the only media node upstream is one the feature flags switch off.
   * The wrapper deliberately hands over no model in that case, so the batch
   * cannot start; this says why, instead of leaving "Connect a media node"
   * showing while a media node is plainly connected.
   */
  unavailableMediaType?: string | null
  submitJob?: (params: BatchJobParams) => Promise<string | null>
  onCreateGalleryContainer?: () => string
  onCreateOutputNode?: (outputUrl: string, index: number, prompt?: string, variationHash?: string) => string
  onCreateBatchFolder?: (batchName: string) => Promise<string | null>
  onMoveFileToBatchFolder?: (fileUuid: string) => Promise<void>
  onResetBatchState?: () => void
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`
  const secs = Math.floor(ms / 1000)
  if (secs < 60) return `${secs}s`
  const mins = Math.floor(secs / 60)
  const remSecs = secs % 60
  if (mins < 60) return `${mins}m ${remSecs}s`
  const hrs = Math.floor(mins / 60)
  const remMins = mins % 60
  return `${hrs}h ${remMins}m`
}

function sendBrowserNotification(title: string, body: string) {
  if (typeof window === 'undefined') return
  if (document.hasFocus()) return
  if (Notification.permission === 'granted') {
    new Notification(title, { body, icon: '/favicon.ico' })
  } else if (Notification.permission !== 'denied') {
    Notification.requestPermission().then((perm) => {
      if (perm === 'granted') {
        new Notification(title, { body, icon: '/favicon.ico' })
      }
    })
  }
}

// ── Main component ──────────────────────────────────────────────────────────

function BatchGeneratorComponent({
  id,
  title,
  selected = false,
  width,
  height,
  onSelect,
  onHoverChange,
  onStateChange,
  state = {},
  connectedPrompts,
  connectedMediaType,
  connectedModelId,
  connectedModelName,
  connectedPurpose,
  connectedSubPurpose,
  connectedSettings,
  connectedNodeType = 'image',
  unavailableMediaType,
  submitJob,
  onCreateGalleryContainer,
  onCreateOutputNode,
  onCreateBatchFolder,
  onMoveFileToBatchFolder,
  onResetBatchState,
}: BatchGeneratorProps) {
  const [batchStatus, setBatchStatus] = useState<BatchStatus>(
    () => (state.batchStatus as BatchStatus) ?? 'idle',
  )
  const [progress, setProgress] = useState<BatchProgress>(
    () => (state.progress as BatchProgress) ?? { total: 0, succeeded: 0, failed: 0, queued: 0, currentIndex: 0, currentSummary: '', estimatedTimeRemainingMs: 0, averageTimePerItemMs: 0 },
  )
  const [cost, setCost] = useState<BatchCost>(
    () => (state.cost as BatchCost) ?? { totalCost: 0, projectedTotal: 0 },
  )
  const [config, setConfig] = useState<BatchConfig>(
    () => (state.config as BatchConfig) ?? { concurrency: 1, maxRetries: 3, pauseOnError: false, skipCompleted: true },
  )
  const [batchItems, setBatchItems] = useState<BatchItem[]>(
    () => (state.batchItems as BatchItem[]) ?? [],
  )
  const [showSettings, setShowSettings] = useState(false)

  // Pause/cancel signals
  const pauseRef = useRef(false)
  const cancelRef = useRef(false)
  const isExecutingRef = useRef(false)

  // Time tracking for estimates
  const batchStartTimeRef = useRef<number>(0)
  const itemTimesRef = useRef<number[]>([])

  // On mount: if saved status is 'running' but no execution is active, reset to idle
  // This handles page refresh where the execution context was lost
  useEffect(() => {
    if ((state.batchStatus === 'running' || state.batchStatus === 'paused') && !isExecutingRef.current) {
      setBatchStatus('idle')
      setProgress({ total: 0, succeeded: 0, failed: 0, queued: 0, currentIndex: 0, currentSummary: '', estimatedTimeRemainingMs: 0, averageTimePerItemMs: 0 })
      setCost({ totalCost: 0, projectedTotal: 0 })
      setBatchItems([])
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Persist state
  const isInitialMount = useRef(true)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }
    onStateChange?.((prev) => ({
      ...prev,
      batchStatus,
      progress,
      cost,
      config,
    }))
  }, [batchStatus, progress, cost, config, onStateChange])

  // ── Derived ───────────────────────────────────────────────────────────

  const promptCount = connectedPrompts?.length ?? 0
  const hasPrompts = promptCount > 0
  const hasModel = !!connectedModelId
  // '3d' reads as "3D" everywhere else in the product, not as a sentence-cased word.
  const unavailableLabel = unavailableMediaType === '3d' ? '3D' : unavailableMediaType
  const canStart = hasPrompts && hasModel && (batchStatus === 'idle' || batchStatus === 'ready') && !isExecutingRef.current
  const isRunning = batchStatus === 'running'
  const isPaused = batchStatus === 'paused'
  const isDone = batchStatus === 'completed' || batchStatus === 'cancelled' || batchStatus === 'error'

  const progressPct = progress.total > 0
    ? Math.round((progress.succeeded + progress.failed) / progress.total * 100)
    : 0

  // ── Execution ────────────────────────────────────────────────────────

  const handleStart = useCallback(async () => {
    // Every line below this one submits jobs that cost the user credits, so the
    // switched-off source is refused here too and not only by hiding the button.
    if (unavailableMediaType) return
    if (!connectedPrompts?.length || !connectedModelId || !submitJob) return
    if (isExecutingRef.current) return

    isExecutingRef.current = true
    cancelRef.current = false
    pauseRef.current = false
    batchStartTimeRef.current = Date.now()
    itemTimesRef.current = []

    const total = connectedPrompts.length
    setBatchStatus('running')
    setProgress({ total, queued: total, succeeded: 0, failed: 0, currentIndex: 0, currentSummary: '', estimatedTimeRemainingMs: 0, averageTimePerItemMs: 0 })
    setCost({ totalCost: 0, projectedTotal: 0 })

    // Create Gallery Container node for output images
    if (onCreateGalleryContainer) {
      onCreateGalleryContainer()
    }

    // Create folder for batch-generated files
    const batchName = title || 'Batch Generator'
    if (onCreateBatchFolder) {
      await onCreateBatchFolder(batchName)
    }

    // Initialize batch items
    const initialItems: BatchItem[] = connectedPrompts.map((_, i) => ({
      index: i,
      promptIndex: i,
      status: 'queued' as const,
      retryCount: 0,
    }))
    setBatchItems(initialItems)

    // Build items for executor
    const items = connectedPrompts.map((prompt, i) => ({
      index: i,
      prompt: prompt.prompt,
      negativePrompt: prompt.negativePrompt,
    }))

    let totalCostAccum = 0

    try {
      const result = await executeBatch({
        items,
        jobTemplate: {
          nodeId: id,
          nodeType: connectedNodeType,
          purpose: connectedPurpose,
          subPurpose: connectedSubPurpose,
          sourceFamily: connectedMediaType ?? undefined,
          modelId: connectedModelId,
          settings: connectedSettings,
        },
        submitJob,
        onItemStart: (index) => {
          const prompt = connectedPrompts[index]
          const summary = prompt?.prompt?.substring(0, 60) || `Item ${index}`
          setProgress((prev) => ({
            ...prev,
            currentIndex: index,
            currentSummary: summary,
            queued: Math.max(0, prev.queued - 1),
          }))
        },
        onItemComplete: (index, itemResult) => {
          const itemDuration = Date.now() - batchStartTimeRef.current
          itemTimesRef.current.push(itemDuration)

          if (itemResult.creditsCost) {
            totalCostAccum += itemResult.creditsCost
          }

          // Create output file node inside Gallery Group
          // Create output file node on canvas (standalone, no parent)
          if (itemResult.outputUrl && onCreateOutputNode) {
            const prompt = connectedPrompts[index]
            onCreateOutputNode(
              itemResult.outputUrl,
              index,
              prompt?.prompt,
              prompt?.variationHash,
            )
          }

          // Move generated file to batch folder (small delay to ensure file record exists)
          if (itemResult.fileUuid && onMoveFileToBatchFolder) {
            setTimeout(() => onMoveFileToBatchFolder(itemResult.fileUuid!), 500)
          }

          setProgress((prev) => {
            const newSucceeded = prev.succeeded + 1
            const processed = newSucceeded + prev.failed
            const avgTime = processed > 0 ? (Date.now() - batchStartTimeRef.current) / processed : 0
            const remaining = prev.total - processed
            return {
              ...prev,
              succeeded: newSucceeded,
              averageTimePerItemMs: avgTime,
              estimatedTimeRemainingMs: avgTime * remaining,
            }
          })

          setCost((prev) => {
            const newTotal = totalCostAccum
            const processed = progress.succeeded + progress.failed + 1
            const projected = processed > 0 ? (newTotal / processed) * progress.total : 0
            return { totalCost: newTotal, projectedTotal: projected }
          })
        },
        onItemError: (index, error) => {
          setProgress((prev) => {
            const newFailed = prev.failed + 1
            const processed = prev.succeeded + newFailed
            const avgTime = processed > 0 ? (Date.now() - batchStartTimeRef.current) / processed : 0
            const remaining = prev.total - processed
            return {
              ...prev,
              failed: newFailed,
              averageTimePerItemMs: avgTime,
              estimatedTimeRemainingMs: avgTime * remaining,
            }
          })

          if (config.pauseOnError) {
            pauseRef.current = true
            setBatchStatus('paused')
          }
        },
        onProgress: (completed, total) => {
          // Already handled in onItemComplete/onItemError
        },
        onBatchItemUpdate: (index, update) => {
          setBatchItems((prev) => {
            const next = [...prev]
            if (next[index]) {
              next[index] = { ...next[index], ...update }
            }
            return next
          })
        },
        shouldPause: () => pauseRef.current,
        shouldCancel: () => cancelRef.current,
        pauseOnError: config.pauseOnError,
        maxRetries: config.maxRetries,
        concurrency: config.concurrency,
        skipCompleted: config.skipCompleted ? batchItems.filter((i) => i.status === 'succeeded') : undefined,
      })

      if (result.cancelled) {
        setBatchStatus('cancelled')
      } else if (result.failed > 0 && result.completed === 0) {
        setBatchStatus('error')
      } else {
        setBatchStatus('completed')
      }

      // Browser notification when done
      const statusLabel = result.cancelled ? 'cancelled' : 'completed'
      sendBrowserNotification(
        `Batch ${statusLabel}`,
        `${result.completed} succeeded, ${result.failed} failed out of ${items.length} total`,
      )
    } catch (err) {
      setBatchStatus('error')
      sendBrowserNotification('Batch error', err instanceof Error ? err.message : 'Unknown error')
    } finally {
      isExecutingRef.current = false
      // Refresh file list once after batch completes
      window.dispatchEvent(new Event('genie:file-created'))
    }
  }, [unavailableMediaType, connectedPrompts, connectedModelId, connectedNodeType, connectedPurpose, connectedSubPurpose, connectedMediaType, connectedSettings, submitJob, id, title, config, batchItems, progress, onCreateGalleryContainer, onCreateOutputNode, onCreateBatchFolder, onMoveFileToBatchFolder])

  const handlePause = useCallback(() => {
    pauseRef.current = true
    setBatchStatus('paused')
  }, [])

  const handleResume = useCallback(() => {
    pauseRef.current = false
    setBatchStatus('running')
  }, [])

  const handleCancel = useCallback(() => {
    cancelRef.current = true
    setBatchStatus('cancelled')
  }, [])

  const handleReset = useCallback(() => {
    setBatchStatus('idle')
    setProgress({ total: 0, succeeded: 0, failed: 0, queued: 0, currentIndex: 0, currentSummary: '', estimatedTimeRemainingMs: 0, averageTimePerItemMs: 0 })
    setCost({ totalCost: 0, projectedTotal: 0 })
    setBatchItems([])
    onResetBatchState?.()
    onStateChange?.((prev) => ({ ...prev, batchStatus: 'idle', batchItems: [], batchFolderId: null }))
  }, [onStateChange, onResetBatchState])

  // ── Status text ───────────────────────────────────────────────────────

  const statusText = useMemo(() => {
    if (unavailableLabel) return `${unavailableLabel} generation is unavailable`
    if (!hasPrompts) return 'Connect Prompt Composer'
    if (!hasModel) return 'Connect a media node'
    if (isRunning) return `Running ${progress.succeeded + progress.failed}/${progress.total}`
    if (isPaused) return `Paused at #${progress.currentIndex}`
    if (batchStatus === 'completed') return `Done · ${progress.succeeded} outputs`
    if (batchStatus === 'cancelled') return `Cancelled · ${progress.succeeded} completed`
    if (batchStatus === 'error') return `Error · ${progress.succeeded} completed, ${progress.failed} failed`
    return `Ready · ${promptCount} prompts`
  }, [unavailableLabel, hasPrompts, hasModel, isRunning, isPaused, batchStatus, progress, promptCount])

  // ── Scroll isolation ──────────────────────────────────────────────────

  const scrollRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const handleWheel = (e: WheelEvent) => {
      const canScrollDown = el.scrollTop < el.scrollHeight - el.clientHeight
      const canScrollUp = el.scrollTop > 0
      if ((e.deltaY > 0 && canScrollDown) || (e.deltaY < 0 && canScrollUp)) {
        e.stopPropagation()
      }
    }
    el.addEventListener('wheel', handleWheel)
    return () => el.removeEventListener('wheel', handleWheel)
  }, [])

  return (
    <NodeContainer
      nodeId={id}
      title={title || 'Batch Generator'}
      icon="play"
      selected={selected}
      nodeType="text"
      width={width}
      onSelect={onSelect}
      onHoverChange={onHoverChange}
      onStateChange={onStateChange}
      state={state}
    >
      <CardContent className="relative flex flex-col flex-1 p-0 overflow-hidden">
        {/* Status header */}
        <div className="flex items-center justify-between px-3 py-1.5 border-b border-border/50">
          <span className="text-[11px] text-muted-foreground tabular-nums">{statusText}</span>
          {connectedMediaType && (
            <span className={cn(
              'text-[10px] px-1.5 py-0.5 rounded capitalize',
              connectedMediaType === 'image' ? 'bg-blue-500/10 text-blue-400' :
              connectedMediaType === 'video' ? 'bg-purple-500/10 text-purple-400' :
              connectedMediaType === '3d' ? 'bg-orange-500/10 text-orange-400' :
              'bg-muted text-muted-foreground'
            )}>
              {connectedMediaType}
            </span>
          )}
        </div>

        {/* Switched-off source: say so, rather than showing nothing to press */}
        {unavailableLabel && (
          <div
            data-testid="batch-source-unavailable"
            className="px-3 py-2 border-b border-border/50 flex items-start gap-2 text-[11px] text-amber-400"
          >
            <Icon name="triangle-alert" size="sm" className="shrink-0 mt-px" />
            <span>
              {unavailableLabel} generation is switched off, so the connected {unavailableLabel} node
              cannot run this batch. Connect an image or video node instead.
            </span>
          </div>
        )}

        {/* Source info */}
        {hasPrompts && hasModel && (
          <div className="px-3 py-1.5 border-b border-border/50 text-[10px] text-muted-foreground">
            {promptCount} prompts · {connectedModelName || connectedModelId || 'model'}{connectedMediaType ? ` · ${connectedMediaType}` : ''}
          </div>
        )}

        {/* Progress bar */}
        {(isRunning || isPaused || isDone) && progress.total > 0 && (
          <div className="px-3 py-2 border-b border-border/50">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs tabular-nums">{progress.succeeded + progress.failed}/{progress.total}</span>
              <span className="text-[10px] text-muted-foreground">{progressPct}%</span>
            </div>
            <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
              <div className="flex h-full">
                <div className="bg-emerald-500 transition-all" style={{ width: `${(progress.succeeded / progress.total) * 100}%` }} />
                <div className="bg-destructive transition-all" style={{ width: `${(progress.failed / progress.total) * 100}%` }} />
              </div>
            </div>
            {progress.estimatedTimeRemainingMs > 0 && isRunning && (
              <div className="text-[10px] text-muted-foreground mt-1">
                ~{formatDuration(progress.estimatedTimeRemainingMs)} remaining
              </div>
            )}
          </div>
        )}

        {/* Current item */}
        {isRunning && progress.currentSummary && (
          <div className="px-3 py-1.5 border-b border-border/50 bg-muted/30">
            <div className="flex items-center gap-2">
              <Icon name="loader" size="sm" className="text-violet-400 animate-spin shrink-0" />
              <span className="text-[11px] text-foreground truncate">
                #{progress.currentIndex} — {progress.currentSummary}
              </span>
            </div>
          </div>
        )}

        {/* Controls */}
        <div className="px-3 py-2 border-b border-border/50 flex items-center gap-1.5">
          {canStart && (
            <Button variant="default" size="sm" onClick={handleStart} className="h-7 flex-1 text-xs gap-1">
              <Icon name="play" size="sm" />
              Start ({promptCount})
            </Button>
          )}
          {isRunning && (
            <Button variant="ghost" size="sm" onClick={handlePause} className="h-7 flex-1 text-xs gap-1">
              <Icon name="pause" size="sm" />
              Pause
            </Button>
          )}
          {isPaused && (
            <Button variant="default" size="sm" onClick={handleResume} className="h-7 flex-1 text-xs gap-1">
              <Icon name="play" size="sm" />
              Resume
            </Button>
          )}
          {(isRunning || isPaused) && (
            <Button variant="ghost" size="sm" onClick={handleCancel} className="h-7 text-xs text-destructive gap-1">
              <Icon name="square" size="sm" />
              Cancel
            </Button>
          )}
          {isDone && (
            <Button variant="ghost" size="sm" onClick={handleReset} className="h-7 flex-1 text-xs gap-1">
              <Icon name="rotate-ccw" size="sm" />
              Reset
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowSettings(!showSettings)}
            className="h-7 px-1.5 text-xs"
          >
            <Icon name="settings" size="sm" className="text-muted-foreground" />
          </Button>
        </div>

        {/* Settings */}
        {showSettings && (
          <div className="px-3 py-2 border-b border-border/50 space-y-2 bg-muted/20">
            <div className="flex items-center justify-between">
              <span className="text-[11px] text-foreground">Concurrency</span>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4].map((n) => (
                  <button
                    key={n}
                    onClick={() => setConfig((prev) => ({ ...prev, concurrency: n }))}
                    className={cn(
                      'w-6 h-6 rounded text-[10px] font-medium transition-colors',
                      config.concurrency === n ? 'bg-foreground text-background' : 'bg-muted text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[11px] text-foreground">Max retries</span>
              <div className="flex items-center gap-1">
                {[0, 1, 3, 5].map((n) => (
                  <button
                    key={n}
                    onClick={() => setConfig((prev) => ({ ...prev, maxRetries: n }))}
                    className={cn(
                      'w-6 h-6 rounded text-[10px] font-medium transition-colors',
                      config.maxRetries === n ? 'bg-foreground text-background' : 'bg-muted text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[11px] text-foreground">Pause on error</span>
              <button
                onClick={() => setConfig((prev) => ({ ...prev, pauseOnError: !prev.pauseOnError }))}
                className={cn(
                  'text-[10px] px-2 py-0.5 rounded transition-colors',
                  config.pauseOnError ? 'bg-amber-500/20 text-amber-400' : 'bg-muted text-muted-foreground',
                )}
              >
                {config.pauseOnError ? 'Yes' : 'No'}
              </button>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[11px] text-foreground">Skip completed</span>
              <button
                onClick={() => setConfig((prev) => ({ ...prev, skipCompleted: !prev.skipCompleted }))}
                className={cn(
                  'text-[10px] px-2 py-0.5 rounded transition-colors',
                  config.skipCompleted ? 'bg-emerald-500/20 text-emerald-400' : 'bg-muted text-muted-foreground',
                )}
              >
                {config.skipCompleted ? 'Yes' : 'No'}
              </button>
            </div>
          </div>
        )}

        {/* Stats */}
        {(progress.succeeded > 0 || progress.failed > 0) && (
          <div className="px-3 py-1.5 border-b border-border/50 space-y-0.5">
            {progress.succeeded > 0 && (
              <div className="flex items-center gap-2 text-xs">
                <Icon name="check" size="sm" className="text-emerald-500" />
                <span className="text-emerald-500 tabular-nums">{progress.succeeded} succeeded</span>
              </div>
            )}
            {progress.failed > 0 && (
              <div className="flex items-center gap-2 text-xs">
                <Icon name="x" size="sm" className="text-destructive" />
                <span className="text-destructive tabular-nums">{progress.failed} failed</span>
              </div>
            )}
          </div>
        )}

        {/* Cost */}
        {cost.totalCost > 0 && (
          <div className="px-3 py-1.5 flex items-center justify-between text-[10px]">
            <span className="text-muted-foreground">Cost</span>
            <span className="text-foreground tabular-nums">
              ${cost.totalCost.toFixed(2)}
              {cost.projectedTotal > cost.totalCost && (
                <span className="text-muted-foreground"> · ~${cost.projectedTotal.toFixed(2)} projected</span>
              )}
            </span>
          </div>
        )}

        {/* Elapsed time */}
        {isDone && progress.averageTimePerItemMs > 0 && (
          <div className="px-3 py-1.5 flex items-center justify-between text-[10px]">
            <span className="text-muted-foreground">Avg per item</span>
            <span className="text-foreground tabular-nums">
              {formatDuration(progress.averageTimePerItemMs)}
            </span>
          </div>
        )}

        {/* Empty state — the notice above already says what to do when a
            switched-off node is the thing that is connected */}
        {!hasPrompts && !hasModel && !unavailableLabel && (
          <div className="flex-1 flex items-center justify-center py-6 text-xs text-muted-foreground">
            Connect prompts + media node
          </div>
        )}
      </CardContent>
    </NodeContainer>
  )
}

export const BatchGenerator = withNodeOptimization(BatchGeneratorComponent, {
  compareProps: ['id', 'title', 'selected', 'state', 'connectedPrompts', 'connectedMediaType', 'connectedModelId', 'connectedNodeType', 'unavailableMediaType'],
})
